/**
 * EmailSourcesTab — Admin Client Detail
 * CRUD for MailboxConfiguration entities (list/detail/create views).
 */
import { useCallback, useEffect, useState } from "react";
import { Plus } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { useAdminApi } from "@/services/api/admin-api";
import type { MailboxConfigurationDto } from "@/types/api/admin";
import { ConnectionStatusBadge } from "./email-sources/ConnectionStatusBadge";
import { EmailSourceForm } from "./email-sources/EmailSourceForm";
import { OAuthConnectButton } from "./email-sources/OAuthConnectButton";

// =============================================================================
// TYPES
// =============================================================================

interface EmailSourcesTabProps {
    clientId: number;
}

type ViewMode = "list" | "detail" | "create";

// =============================================================================
// EMAIL SOURCE LIST SECTION
// =============================================================================

function EmailSourceListSection({
    clientId,
    onSelectSource,
    onCreateSource,
    onRefresh,
}: {
    clientId: number;
    onSelectSource: (id: number) => void;
    onCreateSource: () => void;
    onRefresh: number; // increment to force reload
}) {
    const adminApi = useAdminApi();

    const [sources, setSources] = useState<MailboxConfigurationDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [disconnectingId, setDisconnectingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [restoringId, setRestoringId] = useState<number | null>(null);

    const fetchSources = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listEmailSources(clientId);
            setSources(res?.data ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load email sources.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchSources();
    }, [fetchSources, onRefresh]);

    async function handleDisconnect(source: MailboxConfigurationDto) {
        if (!confirm(`Disconnect ${source.emailAddress}? This will revoke its OAuth tokens.`)) return;
        setDisconnectingId(source.id);
        try {
            await adminApi.disconnectEmailSource(clientId, source.id);
            await fetchSources();
        } catch (err) {
            alert(err instanceof Error ? err.message : "Failed to disconnect.");
        } finally {
            setDisconnectingId(null);
        }
    }

    async function handleRestore(source: MailboxConfigurationDto) {
        setRestoringId(source.id);
        try {
            await adminApi.updateEmailSource(clientId, source.id, { isEnabled: true });
            await fetchSources();
        } catch (err) {
            alert(err instanceof Error ? err.message : "Failed to restore.");
        } finally {
            setRestoringId(null);
        }
    }

    function canRestore(source: MailboxConfigurationDto): boolean {
        return !sources.some((s) => s.id !== source.id && s.isEnabled && s.emailAddress === source.emailAddress);
    }

    async function handleDelete(source: MailboxConfigurationDto) {
        if (!confirm(`Delete ${source.emailAddress}? This will deactivate the mailbox and revoke its OAuth tokens.`)) return;
        setDeletingId(source.id);
        try {
            await adminApi.deleteEmailSource(clientId, source.id);
            await fetchSources();
        } catch (err) {
            alert(err instanceof Error ? err.message : "Failed to delete.");
        } finally {
            setDeletingId(null);
        }
    }

    function handleConnected() {
        fetchSources();
    }

    function formatInterval(ms: number) {
        const sec = Math.round(ms / 1000);
        if (sec < 60) return `${sec}s`;
        const min = Math.round(sec / 60);
        return `${min}m`;
    }

    function formatDate(iso: string | null) {
        if (!iso) return "—";
        return new Date(iso).toLocaleString();
    }

    return (
        <div>
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Email Sources</h3>
                    <p className="text-sm text-tertiary">Configure M365 mailboxes to ingest PDF attachments automatically.</p>
                </div>
                <Button color="primary" size="sm" iconLeading={Plus} onClick={onCreateSource}>
                    Add Email Source
                </Button>
            </div>

            {/* Error */}
            {error && <p className="mb-3 text-sm text-error-primary">{error}</p>}

            {/* Loading */}
            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" label="Loading email sources..." />
                </div>
            ) : sources.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No email sources configured.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-secondary">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-secondary bg-secondary_subtle">
                            <tr>
                                <th className="px-4 py-3 font-medium text-secondary">Email Address</th>
                                <th className="px-4 py-3 font-medium text-secondary">Type</th>
                                <th className="px-4 py-3 font-medium text-secondary">Organization</th>
                                <th className="px-4 py-3 font-medium text-secondary">Status</th>
                                <th className="px-4 py-3 font-medium text-secondary">Last Polled</th>
                                <th className="px-4 py-3 font-medium text-secondary">Interval</th>
                                <th className="px-4 py-3 font-medium text-secondary">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary">
                            {sources.map((s) => (
                                <tr key={s.id} className={`${s.isEnabled ? "hover:bg-primary_hover" : "opacity-50"}`}>
                                    <td
                                        className={`px-4 py-3 font-medium text-primary ${s.isEnabled ? "cursor-pointer" : ""}`}
                                        onClick={s.isEnabled ? () => onSelectSource(s.id) : undefined}
                                    >
                                        {s.emailAddress}
                                        {!s.isEnabled && <span className="ml-2 text-xs text-tertiary">(disabled)</span>}
                                    </td>
                                    <td className="px-4 py-3 text-tertiary">{s.mailboxType}</td>
                                    <td className="px-4 py-3 text-tertiary">{s.organizationName}</td>
                                    <td className="px-4 py-3">
                                        <ConnectionStatusBadge status={s.connectionStatus} />
                                    </td>
                                    <td className="px-4 py-3 text-tertiary">{formatDate(s.lastPolledAt)}</td>
                                    <td className="px-4 py-3 text-tertiary">{formatInterval(s.pollIntervalMs)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {s.isEnabled ? (
                                                <>
                                                    <OAuthConnectButton
                                                        clientId={clientId}
                                                        sourceId={s.id}
                                                        connectionStatus={s.connectionStatus}
                                                        onConnected={handleConnected}
                                                    />
                                                    {s.connectionStatus === "Connected" && (
                                                        <Button
                                                            color="tertiary-destructive"
                                                            size="sm"
                                                            isDisabled={disconnectingId === s.id}
                                                            onClick={() => handleDisconnect(s)}
                                                        >
                                                            {disconnectingId === s.id ? "Disconnecting..." : "Disconnect"}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        color="tertiary-destructive"
                                                        size="sm"
                                                        isDisabled={deletingId === s.id}
                                                        onClick={() => handleDelete(s)}
                                                    >
                                                        {deletingId === s.id ? "Deleting..." : "Delete"}
                                                    </Button>
                                                </>
                                            ) : canRestore(s) ? (
                                                <Button color="secondary" size="sm" isDisabled={restoringId === s.id} onClick={() => handleRestore(s)}>
                                                    {restoringId === s.id ? "Restoring..." : "Restore"}
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-tertiary">Email in use by another source</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// EMAIL SOURCES TAB (EXPORT)
// =============================================================================

export function EmailSourcesTab({ clientId }: EmailSourcesTabProps) {
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
    const [listKey, setListKey] = useState(0);

    const handleSelectSource = useCallback((id: number) => {
        setSelectedSourceId(id);
        setViewMode("detail");
    }, []);

    const handleCreateSource = useCallback(() => {
        setSelectedSourceId(null);
        setViewMode("create");
    }, []);

    const handleBack = useCallback(() => {
        setViewMode("list");
        setSelectedSourceId(null);
    }, []);

    const handleSaved = useCallback(() => {
        setListKey((k) => k + 1);
    }, []);

    if (viewMode === "detail" && selectedSourceId != null) {
        return <EmailSourceForm clientId={clientId} sourceId={selectedSourceId} isCreate={false} onBack={handleBack} onSaved={handleSaved} />;
    }

    if (viewMode === "create") {
        return <EmailSourceForm clientId={clientId} isCreate onBack={handleBack} onSaved={handleSaved} />;
    }

    return <EmailSourceListSection clientId={clientId} onSelectSource={handleSelectSource} onCreateSource={handleCreateSource} onRefresh={listKey} />;
}
